import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import { environment } from "../config/environment";
import { createDebugLogger } from "@utils/debugLog";

const log = createDebugLogger("push-notifications");

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PushNotificationStatus {
  isSupported: boolean;
  isSubscribed: boolean;
  isInProgress: boolean;
}

export interface PushNotificationContextValue {
  pushStatus: PushNotificationStatus;
  toggle: () => void;
}

interface PushPayload {
  auth: string;
  p256DH: string;
  endpoint: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Converts a base64url VAPID key to the Uint8Array the Push API expects. */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

/** Extracts the keys from a serialised PushSubscription. */
function subscriptionToPayload(sub: PushSubscription): PushPayload {
  const json = sub.toJSON();
  return {
    auth: json.keys!["auth"],
    p256DH: json.keys!["p256dh"],
    endpoint: json.endpoint!,
  };
}

// ── Context ───────────────────────────────────────────────────────────────────

const PushNotificationContext = createContext<
  PushNotificationContextValue | undefined
>(undefined);

export const PushNotificationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, accessToken } = useAuth();

  const [swRegistration, setSwRegistration] =
    useState<ServiceWorkerRegistration | null>(null);
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isInProgress, setIsInProgress] = useState(false);

  // ── API helpers ─────────────────────────────────────────────────────────────

  const callApi = useCallback(
    async (path: string, payload: PushPayload): Promise<void> => {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }
      const res = await fetch(path, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Push API ${path} returned ${res.status}`);
    },
    [accessToken],
  );

  // ── Subscribe / unsubscribe ──────────────────────────────────────────────────

  const subscribe = useCallback(
    async (reg: ServiceWorkerRegistration): Promise<void> => {
      setIsInProgress(true);
      try {
        const applicationServerKey = urlBase64ToUint8Array(
          environment.applicationServerPublicKey,
        );
        const pushSub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey.buffer as ArrayBuffer,
        });
        await callApi("/api/pushsubscriptions", subscriptionToPayload(pushSub));
        setIsSubscribed(true);
      } catch (err) {
        console.warn("[PushNotification] Subscribe failed:", err);
      } finally {
        setIsInProgress(false);
      }
    },
    [callApi],
  );

  const unsubscribe = useCallback(
    async (reg: ServiceWorkerRegistration): Promise<void> => {
      setIsInProgress(true);
      try {
        const existing = await reg.pushManager.getSubscription();
        if (!existing) return;
        const payload = subscriptionToPayload(existing);
        await existing.unsubscribe();
        await callApi("/api/pushsubscriptions/unsubscribe", payload);
        setIsSubscribed(false);
      } catch (err) {
        console.error("[PushNotification] Unsubscribe failed:", err);
      } finally {
        setIsInProgress(false);
      }
    },
    [callApi],
  );

  // ── Service Worker registration (once authenticated) ────────────────────────

  useEffect(() => {
    if (!isAuthenticated) return;

    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setIsSupported(false);
      return;
    }

    setIsSupported(true);

    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => {
        log("Service Worker registered");
        setSwRegistration(reg);
      })
      .catch((err) => {
        console.error(
          "[PushNotification] Service Worker registration failed:",
          err,
        );
      });
  }, [isAuthenticated]);

  // ── Check existing subscription + auto-prompt on first visit ────────────────

  useEffect(() => {
    if (!swRegistration) return;
    let cancelled = false;

    const initPush = async () => {
      const existing = await swRegistration.pushManager.getSubscription();
      if (cancelled) return;

      if (existing) {
        setIsSubscribed(true);
        return;
      }

      setIsSubscribed(false);

      // Auto-subscribe once the first time the user visits (mirrors Angular behaviour)
      if (!localStorage.getItem("havePromptedForSubscription")) {
        localStorage.setItem("havePromptedForSubscription", "true");
        if (!cancelled) await subscribe(swRegistration);
      }
    };

    initPush().catch((err) =>
      console.error("[PushNotification] Init failed:", err),
    );

    return () => {
      cancelled = true;
    };
  }, [swRegistration, subscribe]);

  // ── Public toggle ────────────────────────────────────────────────────────────

  const toggle = useCallback(() => {
    if (!swRegistration || isInProgress) return;
    if (isSubscribed) {
      unsubscribe(swRegistration);
    } else {
      subscribe(swRegistration);
    }
  }, [swRegistration, isInProgress, isSubscribed, subscribe, unsubscribe]);

  return (
    <PushNotificationContext.Provider
      value={{
        pushStatus: { isSupported, isSubscribed, isInProgress },
        toggle,
      }}
    >
      {children}
    </PushNotificationContext.Provider>
  );
};

export const usePushNotifications = (): PushNotificationContextValue => {
  const ctx = useContext(PushNotificationContext);
  if (!ctx)
    throw new Error(
      "usePushNotifications must be used within PushNotificationProvider",
    );
  return ctx;
};
